/**
 * Auto Gift Product - Múltiples Promociones
 * Agrega automáticamente productos de regalo cuando el carrito alcanza montos mínimos
 * Las promociones no se acumulan: si se alcanza un monto mayor, se elimina el producto de la promoción menor
 */

(function() {
  'use strict';

  // Configuración desde el theme (pasada desde theme.liquid)
  function getConfig() {
    if (!window.theme || !window.theme.autoGift) {
      return {
        enabled: false,
        promotions: []
      };
    }
    
    const rawPromotions = window.theme.autoGift.promotions || [];
    
    // Filtrar y limpiar las promociones válidas
    const promotions = rawPromotions
      .map((promo, index) => {
        const productOrVariantId = promo.productOrVariantId;
        const cleanId = (productOrVariantId && typeof productOrVariantId === 'string') 
          ? productOrVariantId.trim() 
          : (productOrVariantId || '');
        
        if (!cleanId) return null;
        
        return {
          id: promo.id || `promo${index + 1}`,
          productOrVariantId: cleanId,
          minimumAmount: promo.minimumAmount || 0,
          resolvedVariantId: null // Se resolverá después
        };
      })
      .filter(promo => promo !== null && promo.productOrVariantId)
      .sort((a, b) => a.minimumAmount - b.minimumAmount); // Ordenar por monto menor a mayor
    
    return {
      enabled: window.theme.autoGift.enabled || false,
      promotions: promotions
    };
  }
  
  let config = getConfig();

  // Estado
  let isChecking = false;
  let isAutoAdding = false; // Bandera para indicar que estamos agregando productos automáticamente

  /**
   * Obtiene el estado actual del carrito
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Error al obtener el carrito');
      return await response.json();
    } catch (error) {
      console.error('Error al obtener el carrito:', error);
      return null;
    }
  }

  /**
   * Obtiene el variant ID desde un product ID o variant ID
   * Si es un product ID, obtiene la primera variante del producto
   */
  async function resolveVariantId(productOrVariantId, promoId) {
    if (!productOrVariantId) return null;

    const id = productOrVariantId.toString().trim();
    if (!id) return null;

    try {
      const productResponse = await fetch(`/products/${id}.js`);
      
      if (productResponse.ok) {
        // Es un product_id, obtener la primera variante
        const product = await productResponse.json();
        if (product.variants && product.variants.length > 0) {
          const availableVariant = product.variants.find(v => v.available) || product.variants[0];
          const variantId = availableVariant.id;
          console.log(`✅ [${promoId}] Product ID detectado (${id}). Usando variant ID: ${variantId}`);
          return variantId;
        } else {
          console.warn(`⚠️ [${promoId}] El producto ${id} no tiene variantes`);
          return null;
        }
      } else {
        // No es un product_id válido, probablemente es un variant_id
        console.log(`ℹ️ [${promoId}] No se encontró como Product ID. Usando como Variant ID: ${id}`);
        return parseInt(id);
      }
    } catch (error) {
      // Si falla el fetch, probablemente es un variant_id, lo usamos directamente
      console.log(`ℹ️ [${promoId}] Error al obtener producto. Usando como Variant ID: ${id}`);
      return parseInt(id);
    }
  }

  /**
   * Resuelve todos los variant IDs de las promociones
   */
  async function resolveAllVariantIds() {
    for (let promo of config.promotions) {
      if (!promo.resolvedVariantId) {
        promo.resolvedVariantId = await resolveVariantId(promo.productOrVariantId, promo.id);
        if (!promo.resolvedVariantId) {
          console.warn(`⚠️ [${promo.id}] No se pudo resolver el variant ID`);
        }
      }
    }
  }

  /**
   * Verifica qué promoción está activa en el carrito
   */
  function getActivePromoInCart(cart) {
    if (!cart || !cart.items) return null;
    
    for (let promo of config.promotions) {
      if (!promo.resolvedVariantId) continue;
      
      const hasGift = cart.items.some(item => 
        item.variant_id === promo.resolvedVariantId || 
        (item.properties && item.properties._gift === 'true' && item.properties._promo_id === promo.id)
      );
      
      if (hasGift) {
        return promo;
      }
    }
    
    return null;
  }

  /**
   * Agrega el producto de regalo de una promoción al carrito
   */
  async function addGiftProduct(promo) {
    if (isAutoAdding || !promo.resolvedVariantId) return false;

    isAutoAdding = true;
    console.log(`🎁 Agregando producto de regalo automático - ${promo.id}...`);
    console.log(`   Variant ID: ${promo.resolvedVariantId}, Monto mínimo: ${promo.minimumAmount / 100} pesos`);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: promo.resolvedVariantId,
          quantity: 1,
          properties: {
            '_gift': 'true',
            '_auto_added': 'true',
            '_promo_id': promo.id
          }
        })
      });

      // Verificar el status de la respuesta
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        console.error(`❌ [${promo.id}] Error al agregar producto de regalo:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        
        if (response.status === 422) {
          console.error(`❌ [${promo.id}] Error 422: El variant ID puede ser incorrecto o el producto no está disponible.`);
        }
        
        return false;
      }

      const data = await response.json();

      // Verificar si hay errores en la respuesta JSON
      if (data.error || data.status) {
        const errorMsg = data.description || data.message || data.error || 'Error desconocido';
        console.warn(`⚠️ [${promo.id}] No se pudo agregar el producto de regalo:`, errorMsg);
        return false;
      }

      // Éxito
      console.log(`✅ [${promo.id}] Producto de regalo agregado automáticamente`);
      
      // Esperar un poco antes de refrescar para evitar bucles
      setTimeout(() => {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));

        if (typeof refreshCart === 'function') {
          refreshCart();
        }
      }, 1000);

      return true;
    } catch (error) {
      console.error(`❌ [${promo.id}] Error al agregar producto de regalo:`, error);
      return false;
    } finally {
      setTimeout(() => {
        isAutoAdding = false;
      }, 2000);
    }
  }

  /**
   * Remueve el producto de regalo de una promoción del carrito
   */
  async function removeGiftProduct(cart, promo) {
    if (!cart || !cart.items || !promo.resolvedVariantId) return false;

    const giftItem = cart.items.find(item => 
      item.variant_id === promo.resolvedVariantId || 
      (item.properties && item.properties._gift === 'true' && item.properties._promo_id === promo.id)
    );

    if (giftItem) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 0
          })
        });
        console.log(`🗑️ [${promo.id}] Producto de regalo removido del carrito`);
        return true;
      } catch (error) {
        console.error(`❌ [${promo.id}] Error al remover producto de regalo:`, error);
        return false;
      }
    }
    
    return false;
  }

  /**
   * Verifica el carrito y agrega/remueve productos de regalo según corresponda
   */
  async function checkAndUpdateGift() {
    if (!config.enabled || config.promotions.length === 0 || isChecking) return;
    
    isChecking = true;

    try {
      // Resolver variant IDs si aún no están resueltos
      await resolveAllVariantIds();
      
      const cart = await getCart();
      if (!cart) {
        isChecking = false;
        return;
      }

      const cartTotal = cart.total_price;
      const activePromo = getActivePromoInCart(cart);
      
      // Determinar qué promoción debería estar activa (la mayor que se alcance)
      let targetPromo = null;
      for (let i = config.promotions.length - 1; i >= 0; i--) {
        const promo = config.promotions[i];
        if (cartTotal >= promo.minimumAmount && promo.resolvedVariantId) {
          targetPromo = promo;
          break;
        }
      }

      // Verificar si la promoción activa está por debajo de su mínimo
      if (activePromo && cartTotal < activePromo.minimumAmount) {
        // El carrito está por debajo del mínimo de la promoción activa, removerla
        console.log(`📉 Carrito (${cartTotal / 100}) está por debajo del mínimo de ${activePromo.id} (${activePromo.minimumAmount / 100}). Removiendo producto.`);
        await removeGiftProduct(cart, activePromo);
        
        // Después de remover, verificar si hay otra promoción que debería estar activa
        if (targetPromo && targetPromo.id !== activePromo.id) {
          console.log(`📈 Agregando producto de ${targetPromo.id} (${targetPromo.minimumAmount / 100} pesos)`);
          await addGiftProduct(targetPromo);
        } else {
          // Refrescar el carrito después de remover
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
          if (typeof refreshCart === 'function') {
            setTimeout(() => refreshCart(), 500);
          }
        }
      }
      // Si hay una promoción activa diferente a la que debería estar (y está por encima de su mínimo)
      else if (activePromo && targetPromo && activePromo.id !== targetPromo.id) {
        // Si la promoción activa es menor que la objetivo, cambiar a la mayor
        if (activePromo.minimumAmount < targetPromo.minimumAmount) {
          console.log(`🔄 Cambiando de promoción: ${activePromo.id} (${activePromo.minimumAmount / 100}) → ${targetPromo.id} (${targetPromo.minimumAmount / 100})`);
          await removeGiftProduct(cart, activePromo);
          await addGiftProduct(targetPromo);
        }
        // Si la promoción activa es mayor que la objetivo, cambiar a la menor
        else if (activePromo.minimumAmount > targetPromo.minimumAmount) {
          console.log(`🔄 Cambiando de promoción: ${activePromo.id} (${activePromo.minimumAmount / 100}) → ${targetPromo.id} (${targetPromo.minimumAmount / 100})`);
          await removeGiftProduct(cart, activePromo);
          await addGiftProduct(targetPromo);
        }
      }
      // Si hay una promoción activa pero el carrito está bajo todos los mínimos, removerla
      else if (activePromo && !targetPromo) {
        console.log(`📉 Carrito bajo todos los mínimos. Removiendo producto de ${activePromo.id}`);
        await removeGiftProduct(cart, activePromo);
        // Refrescar el carrito después de remover
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
        if (typeof refreshCart === 'function') {
          setTimeout(() => refreshCart(), 500);
        }
      }
      // Si no hay promoción activa pero debería haber una, agregarla
      else if (!activePromo && targetPromo) {
        console.log(`📈 Carrito alcanzó el mínimo de ${targetPromo.id} (${targetPromo.minimumAmount / 100} pesos)`);
        await addGiftProduct(targetPromo);
      }
      
    } catch (error) {
      console.error('Error al verificar producto de regalo:', error);
    } finally {
      isChecking = false;
    }
  }

  /**
   * Inicializa el sistema de producto de regalo automático
   */
  async function initAutoGift() {
    if (!config.enabled || config.promotions.length === 0) {
      console.log('Producto de regalo automático desactivado o no configurado');
      return;
    }

    console.log('🎁 Sistema de producto de regalo automático activado');
    console.log(`   Promociones configuradas: ${config.promotions.length}`);
    config.promotions.forEach(promo => {
      console.log(`   - ${promo.id}: Monto mínimo ${promo.minimumAmount / 100} pesos`);
    });

    // Resolver todos los variant IDs al inicializar
    await resolveAllVariantIds();

    // Verificar al cargar la página
    checkAndUpdateGift();

    // Escuchar eventos de actualización del carrito
    document.addEventListener('cart:item-added', () => {
      if (!isAutoAdding) {
        setTimeout(checkAndUpdateGift, 1000);
      }
    });

    document.addEventListener('cart:refresh', () => {
      if (!isAutoAdding) {
        setTimeout(checkAndUpdateGift, 1000);
      }
    });

    document.addEventListener('line-item:change:end', () => {
      if (!isAutoAdding) {
        setTimeout(checkAndUpdateGift, 1000);
      }
    });

    // Interceptar las funciones de agregar al carrito existentes
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      if (typeof url === 'string' && (url.includes('/cart/add.js') || url.includes('/cart/change.js'))) {
        return originalFetch.apply(this, args).then(response => {
          if (!isAutoAdding) {
            setTimeout(checkAndUpdateGift, 1000);
          }
          return response;
        });
      }
      return originalFetch.apply(this, args);
    };
  }

  // Inicializar cuando el DOM esté listo y el objeto theme esté disponible
  function waitForTheme() {
    if (window.theme && window.theme.autoGift) {
      // Actualizar la configuración por si cambió
      config = getConfig();
      initAutoGift();
    } else {
      // Esperar un poco más si el theme aún no está disponible
      setTimeout(waitForTheme, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForTheme);
  } else {
    waitForTheme();
  }
})();
